import { HijriAdjustment } from '../configs/hijri-adjustments'

// The rules for a downloaded adjustment list: what a payload must look like to
// be trusted, and how it combines with the built-in one. Kept apart from
// hijri-adjustments.ts, which owns the storage and network side, so these stay
// pure — they decide what the calendar shows and are worth testing directly.

export interface IAdjustmentPayload {
    version: number // monotonic; an older payload is ignored
    adjustments: HijriAdjustment[]
}

// Guards against a corrupt or hostile payload shifting the calendar by years.
const MIN_YEAR = 1400
const MAX_YEAR = 1600
const MAX_SHIFT = 2

function isAdjustment(entry: unknown): entry is HijriAdjustment {
    if (!Array.isArray(entry) || entry.length !== 3) return false
    const [hy, hm, shift] = entry
    return Number.isInteger(hy) && hy >= MIN_YEAR && hy <= MAX_YEAR
        && Number.isInteger(hm) && hm >= 1 && hm <= 12
        && Number.isInteger(shift) && Math.abs(shift) <= MAX_SHIFT
}

// Rejects a payload of the wrong shape outright, but only drops the individual
// entries that are bad: one malformed month must not block an urgent fix to
// another one.
export function validate(payload: unknown): IAdjustmentPayload | null {
    if (!payload || typeof payload !== 'object') return null
    const { version, adjustments } = payload as IAdjustmentPayload
    if (!Number.isInteger(version) || version < 0) return null
    if (!Array.isArray(adjustments)) return null
    return { version, adjustments: adjustments.filter(isAdjustment) }
}

// Built-in list first so a downloaded entry for the same month wins. An
// explicit 0 is how a built-in shift that turned out to be wrong gets retracted.
export function merge(builtIn: HijriAdjustment[], downloaded: HijriAdjustment[]): HijriAdjustment[] {
    const byMonth = new Map<number, HijriAdjustment>()
    for (const a of builtIn) byMonth.set(a[0] * 12 + a[1], a)
    for (const a of downloaded) byMonth.set(a[0] * 12 + a[1], a)
    return [...byMonth.values()]
}
